import React, { useCallback, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Info, Loader2 } from 'lucide-react';
import { apiCall } from '../_shared';
import { RatePushModal } from '../stats/RatePushModal';
import { GridHead, RoomBlock } from './CalendarGrid';
import { CellEditor } from './CellEditor';
import { MonthView } from './MonthView';
import { fmtLong, monthEnd, monthStart, plusDays, rangeDays, today } from './calUtils';

const DAYS = 31;

const Toolbar = ({ data, view, setView, roomId, setRoomId, start, setStart, perGuest, setPerGuest, restrictions, setRestrictions, end, onBulk }) => {
  const rooms = data?.rooms || [];
  const opts = rooms.map((r) => <option key={r.room_id} value={r.room_id}>{r.room}</option>);
  const sync = data?.last_sync ? new Date(data.last_sync).toLocaleString('en-GB', { timeZone: 'Europe/Podgorica', dateStyle: 'medium', timeStyle: 'short' }) : '—';
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-xl font-bold text-slate-900">Calendar</h3>
        <div className="ml-auto flex items-center gap-2">
          <select value={view} onChange={(e) => setView(e.target.value)} className="border rounded px-2 py-1.5 text-xs" data-testid="rc-view"><option value="list">List view</option><option value="month">Monthly view</option></select>
          <span title="Rates, rooms to sell and restrictions are read live from Beds24 and pushed back on save." className="p-1.5 border rounded text-slate-500"><Info className="w-3.5 h-3.5" /></span>
          <button type="button" onClick={onBulk} className="border border-[#0071c2] text-[#0071c2] rounded px-3 py-1.5 text-xs font-semibold hover:bg-blue-50" data-testid="rc-bulk-edit">Bulk edit</button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3 text-xs">
        <select value={roomId} onChange={(e) => setRoomId(e.target.value)} className="border rounded px-2 py-1.5 min-w-[200px]" data-testid="rc-room-filter"><option value="all">All Rooms</option>{opts}</select>
        <span className="border rounded px-2 py-1 text-[11px] text-slate-600 bg-slate-50" data-testid="rc-xml-badge">Beds24 (editable)</span>
        <span className="text-slate-500">Last sync: <b className="text-slate-700" data-testid="rc-last-sync">{sync}</b></span>
      </div>
      <div className="flex flex-wrap items-center gap-3 text-xs">
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => setStart(plusDays(start, -DAYS))} className="p-1.5 border rounded hover:bg-slate-50" data-testid="rc-prev"><ChevronLeft className="w-3.5 h-3.5" /></button>
          <label className="border rounded px-2 py-1 flex items-center gap-2"><span className="font-semibold text-slate-800" data-testid="rc-range-label">{fmtLong(start)} – {fmtLong(end)}</span><input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="text-xs" data-testid="rc-start" /></label>
          <button type="button" onClick={() => setStart(plusDays(start, DAYS))} className="p-1.5 border rounded hover:bg-slate-50" data-testid="rc-next"><ChevronRight className="w-3.5 h-3.5" /></button>
        </div>
        <label className="inline-flex items-center gap-1.5"><input type="checkbox" checked={perGuest} onChange={(e) => setPerGuest(e.target.checked)} data-testid="rc-per-guest" /> Pricing per guest</label>
        <label className="inline-flex items-center gap-1.5"><input type="checkbox" checked={restrictions} onChange={(e) => setRestrictions(e.target.checked)} data-testid="rc-restrictions" /> Restrictions</label>
      </div>
    </div>
  );
};

export const RatesCalendar = ({ onBulk }) => {
  const [start, setStart] = useState(today());
  const [view, setView] = useState('list');
  const [roomId, setRoomId] = useState('all');
  const [perGuest, setPerGuest] = useState(false);
  const [restrictions, setRestrictions] = useState(true);
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [sel, setSel] = useState(null);
  const [req, setReq] = useState(null);
  const from = view === 'month' ? monthStart(start) : start;
  const end = view === 'month' ? monthEnd(start) : plusDays(start, DAYS - 1);
  const load = useCallback(async () => {
    setErr('');
    try { setData(await apiCall(`/api/admin/rates/calendar?from_date=${from}&to_date=${end}`)); } catch (e) { setData(null); setErr(e.message || 'Beds24 unavailable'); }
  }, [from, end]);
  useEffect(() => { load(); }, [load]);
  const days = rangeDays(from, end);
  const allRooms = data?.rooms || [];
  const rooms = roomId === 'all' ? allRooms : allRooms.filter((r) => String(r.room_id) === String(roomId));
  const onPick = (room, iso, field, to) => setSel({ room, iso, to, field, d: room.days[iso] });
  const blocks = rooms.map((r) => <RoomBlock key={r.room_id} room={r} days={days} perGuest={perGuest} restrictions={restrictions} onPick={onPick} />);
  const toolbar = { data, view, setView, roomId, setRoomId, start, setStart, perGuest, setPerGuest, restrictions, setRestrictions, end, onBulk };
  return (
    <div className="bg-white rounded-xl border p-4" data-testid="rates-calendar">
      <Toolbar {...toolbar} />
      {err && <p className="text-xs text-red-700 mt-3 bg-red-50 border border-red-200 rounded p-2" data-testid="rc-error">{err}</p>}
      {!data && !err && <p className="text-xs text-slate-500 flex items-center gap-1 mt-3"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Reading Beds24…</p>}
      {data && view === 'list' && (
        <div className="overflow-x-auto mt-3 -mx-4 px-4">
          <table className="border-separate border-spacing-0 text-xs" data-testid="rc-table"><GridHead days={days} />{blocks}</table>
        </div>
      )}
      {data && view === 'month' && rooms[0] && <div className="mt-3"><MonthView room={rooms[0]} start={start} onPick={onPick} /></div>}
      {data && view === 'month' && !rooms[0] && <p className="text-xs text-slate-500 mt-3">No room data.</p>}
      {sel && <CellEditor key={`${sel.room.room_id}-${sel.iso}-${sel.field}`} sel={sel} onClose={() => setSel(null)} onSubmit={(body) => setReq({ path: '/api/admin/rates/push', body, title: `${sel.room.room} · ${body.from_date}${body.to_date !== body.from_date ? ` → ${body.to_date}` : ''}` })} />}
      {req && <RatePushModal title={req.title} request={req} onClose={() => { setReq(null); setSel(null); }} onDone={() => load()} />}
    </div>
  );
};
